import Intercom from '@intercom/messenger-js-sdk'
import type { UserInfo } from '@cobu/types'
import { userService } from '@cobu/api-client'
import { logger } from './utils/logger'

const appId = import.meta.env.VITE_INTERCOM_APP_ID as string | undefined
let unreadListenerSetup = false
// Booting with a user JWT creates the Intercom contact, so residents without one are
// only booted once they actually ask for support — not on session start.
let bootPromise: Promise<boolean> | null = null

function setupUnreadCountListener() {
  if (!appId || unreadListenerSetup) return
  if (typeof window.Intercom !== 'function') return

  try {
    window.Intercom!('onUnreadCountChange', function (unreadCount: number) {
      if (unreadCount > 0 && typeof window.Intercom === 'function') {
        window.Intercom!('show')
      }
    })
    unreadListenerSetup = true
  } catch (error) {
    logger.error('[Intercom] Failed to set up unread count listener:', error)
  }
}

function bootIntercom(intercomJwt: string) {
  Intercom({
    app_id: appId!,
    intercom_user_jwt: intercomJwt,
    hide_default_launcher: true
  })
  setupUnreadCountListener()
}

async function fetchJwtAndBoot(): Promise<boolean> {
  try {
    const { intercomJwt } = await userService.fetchIntercomJwt()
    if (!intercomJwt) return false
    bootIntercom(intercomJwt)
    return true
  } catch (error) {
    logger.error('[Intercom] Boot failed:', error)
    return false
  }
}

function ensureIntercomBooted(): Promise<boolean> {
  if (!bootPromise) {
    bootPromise = fetchJwtAndBoot().then(booted => {
      // Let a later tap retry after a failed boot.
      if (!booted) bootPromise = null
      return booted
    })
  }
  return bootPromise
}

// Residents who already have a contact (`intercomUuid` set) boot on session start so
// outbound messages work and support replies auto-open via the unread listener.
export function bootIntercomForExistingContact(user: UserInfo) {
  if (!appId) return
  if (!user.acceptedTermsAndConditions || !user.intercomUuid) return
  void ensureIntercomBooted()
}

export function shutdownIntercom() {
  if (typeof window.Intercom === 'function') {
    window.Intercom('shutdown')
    unreadListenerSetup = false
  }
  bootPromise = null
}

// Boots on first use, then opens the messenger. The SDK queues commands until its
// script loads, so `show` right after boot is safe.
export async function openIntercom(): Promise<void> {
  if (!appId) return
  if (!(await ensureIntercomBooted())) return
  if (typeof window.Intercom === 'function') {
    window.Intercom('show')
  }
}
